import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Account, accountApi, accountErr } from "../../api/account/accountApi";
import { useUser } from "../../context/UserContext";

interface ProfileSectionProps {
  account: Account;
  onSaved: (account: Account) => void;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({ account, onSaved }) => {
  const { setUser } = useUser();
  const [form, setForm] = useState({
    first_name: account.first_name,
    last_name: account.last_name,
    display_name: account.display_name,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm({
      first_name: account.first_name,
      last_name: account.last_name,
      display_name: account.display_name,
    });
  }, [account]);

  const dirty =
    form.first_name !== account.first_name ||
    form.last_name !== account.last_name ||
    form.display_name !== account.display_name;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("First and last name are required");
      return;
    }
    setBusy(true);
    try {
      const r = await accountApi.updateProfile({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        display_name: form.display_name.trim() || undefined,
      });
      onSaved(r.data);
      // Sidebar / Chatwoot / role helpers all read localStorage user_info.
      try {
        const info = await accountApi.refreshUserInfo();
        setUser(info);
      } catch {
        /* profile is saved; the cached name catches up on next login */
      }
      toast.success("Profile updated");
    } catch (err) {
      toast.error(accountErr(err, "Could not update profile"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="acct-card" onSubmit={save}>
      <div className="acct-card__head">
        <div>
          <h3>Profile</h3>
          <p>How your name appears across the dashboard.</p>
        </div>
      </div>

      <div className="acct-form">
        <label>
          First name
          <input
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            maxLength={200}
            required
          />
        </label>
        <label>
          Last name
          <input
            value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            maxLength={200}
            required
          />
        </label>
        <label className="full">
          Display name <small>(optional — defaults to first + last name)</small>
          <input
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            maxLength={200}
            placeholder={`${form.first_name} ${form.last_name}`.trim()}
          />
        </label>
        <label>
          Username
          <input value={account.username} disabled />
        </label>
        <label>
          Login name
          <input value={account.login_names[0] || account.username} disabled />
        </label>
      </div>

      <div className="acct-actions">
        <button
          type="submit"
          className="acct-btn acct-btn--primary"
          disabled={busy || !dirty}
        >
          {busy ? "Saving..." : "Save changes"}
        </button>
      </div>
    </form>
  );
};

export default ProfileSection;
